import { createAction } from '@reduxjs/toolkit'
import { ResponseData } from 'initAppAPI'
import { l } from '@manychat/manyui'

import {
  AccountDisplayFlag,
  AccountUserDisplayFlagType,
} from 'apps/dashboard/constants/DisplayFlagType'
import { getChannelData } from 'apps/whatsApp/redux/waChannelSlice'
import { alert } from 'common/core'
import {
  AccountProStatusBlockedData,
  AccountUpdatedData,
  AccountFbPolicyNotificationNewData,
  StandardWebSocketData,
  AsyncBillingPaymentSocket,
} from 'common/core/appInterfaces'
import {
  NotificationRemoveType,
  NotificationItem,
  StaticNotificationId,
} from 'common/core/components/NotifyBar/NotifyInterfaces'
import { UPDATE_SKIP_PREVIEW_ONBOARDING_MODAL } from 'common/core/constants/appReduxActionTypes'
import * as atypes from 'common/core/constants/appReduxActionTypes'
import { ChannelType } from 'common/core/constants/ChannelType'
import { IActionWithPayload, IAsyncThunkAction, IThunkAction } from 'common/core/interfaces/actions'
import { ThreadsStatuses } from 'common/core/interfaces/threads'
import { AccountStatus } from 'common/core/models/Account/constants'
import * as settingsActionTypes from 'common/settings/SettingsReduxActionTypes'
import { WebSocketEvents } from 'constants/WebSockets'
import { handleError } from 'shared/api/lib/errors/handlers'
import { AccountApi } from 'shared/api/requests/account'
import { AccountHealthApi } from 'shared/api/requests/accountHealth'
import { ChannelApi } from 'shared/api/requests/channel'
import { settingsApi } from 'shared/api/requests/settings'
import { StaticVersionApi } from 'shared/api/requests/staticVersion'
import { createAsyncAction } from 'shared/lib/redux'
import { isLocal } from 'utils/isLocal'
import { analyticsService } from 'utils/services/analytics'
import {
  anotherTabNotificationsListener,
  accountNotificationsListener,
} from 'utils/services/notificationsService'

import { getUserId, getCurrentAccount, getFetchingCheckVersion } from '../selectors/appSelectors'

export const setProStatusBlocked = (data: AccountProStatusBlockedData) => ({
  type: atypes.SET_PRO_STATUS_BLOCKED,
  payload: data,
})

export const resetCurrentAccount = () => ({
  type: atypes.APP_RESET_CURRENT_ACCOUNT,
})

export const setProStatusUnblocked = (pro_status: AccountStatus) => ({
  type: atypes.SET_PRO_STATUS_UNBLOCKED,
  payload: pro_status,
})

export const setAppData = (initialData: Partial<ResponseData>): IActionWithPayload => ({
  type: atypes.APP_UPDATE,
  data: initialData,
})

export const updateThreadsStats = (data: ThreadsStatuses): IActionWithPayload => ({
  type: atypes.APP_UPDATE_THREADS_STATS,
  data,
})

export const updateConnectedIntegrations = createAction<AccountUpdatedData['model']>(
  'app/updateConnectedIntegrations',
)

export const updateCurrentAccountId = (accountId: AccountId) => ({
  type: atypes.APP_UPDATE_CURRENT_ACCOUNT_ID,
  accountId,
})

export const fetchExtendedAccountData = createAsyncAction(
  'application/fetchExtendedAccountData',
  async () => {
    const response = await settingsApi.getPageExtendedData()
    return response.data
  },
)

export const ensureExtendedAccountData = (): IThunkAction => (dispatch, getState) => {
  if (getCurrentAccount(getState()).isExtendedDataLoaded) {
    return
  }

  dispatch(fetchExtendedAccountData())
}

export const startVersionChecker = createAsyncAction(
  'application/startVersionChecker',
  async () => {
    const response = await StaticVersionApi.checkVersion({ config: { responseType: 'text' } })
    return response.data
  },
  { condition: (_, { getState }) => !isLocal() && !getFetchingCheckVersion(getState()) },
)

export const setNotifications = (notifications: NotificationItem[]) => ({
  type: settingsActionTypes.SETTINGS_NOTIFICATIONS_SET,
  payload: notifications,
})

export const removeNotification =
  ({ notification_id, removeType }: NotificationItem): IThunkAction =>
  async (dispatch) => {
    if (notification_id !== undefined) {
      analyticsService.sendEvent(`NOTIFICATION_BAR.CLOSED`, {
        notification_id,
      })
    }

    if (removeType === NotificationRemoveType.NEVER) return

    if (removeType === NotificationRemoveType.TEMPORARY) {
      return dispatch({
        type: settingsActionTypes.SETTINGS_NOTIFICATION_TEMPORARY_REMOVE,
        notification_id,
      })
    }

    await settingsApi.removeNotification({ query: { notification_id: notification_id.toString() } })

    return dispatch({
      type: settingsActionTypes.SETTINGS_NOTIFICATION_REMOVE_REQUEST,
      notification_id,
    })
  }

export const removeNotificationFromClosedList = (notificationId: StaticNotificationId | number) => {
  return {
    type: settingsActionTypes.SETTINGS_NOTIFICATION_REMOVE_FROM_CLOSED_LIST,
    notificationId,
  }
}

export const readAllPolicyEnforcementNotifications = createAsyncAction(
  'application/readAllPolicyEnforcementNotifications',
  async () => {
    const response = await settingsApi.readAllPolicyEnforcementNotifications()
    return response.data
  },
)

export const handleNewPolicyEnforcementNotifications = (
  data: AccountFbPolicyNotificationNewData,
) => ({
  type: atypes.NEW_POLICY_ENFORCEMENT_NOTIFICATION_NOTIFICATION,
  item: data.model,
})

export const handleReadPolicyEnforcementNotifications =
  (data: StandardWebSocketData): IThunkAction =>
  (dispatch, getState) => {
    if (getUserId(getState()) !== data.model.user_id) {
      return
    }

    dispatch({ type: atypes.READ_POLICY_ENFORCEMENT_NOTIFICATION_NOTIFICATION })
  }

export const handleHighlightLogsOnFbPolicyEnforcementNotification = () => ({
  type: atypes.CURRENT_ACCOUNT_USER_PROFILE_SET_DISPLAY_FLAGS_RESPONSE,
  display_flag: AccountUserDisplayFlagType.HIGHLIGHT_LOGS_ON_FB_POLICY_ENFORCEMENT,
  value: true,
})

export const setRnCreatedFlag = (channel: ChannelType) => ({
  type: atypes.ACCOUNT_SET_DISPLAY_FLAG_RESPONSE,
  display_flag:
    channel === ChannelType.FB
      ? AccountDisplayFlag.RN_DEFAULT_FLOW_CREATED
      : AccountDisplayFlag.IG_RN_DEFAULT_FLOW_CREATED,
  value: true,
})

export const forceResetStateForTesting = () => ({ type: atypes.FORCE_UPDATE_STATE })

/** This request get 302 and redirect to /addFbAccount?account_id=*** */
export const connectFBChannel = createAsyncAction(
  'application/connectFBChannel',
  async (options?: { returnURL: string }) => {
    const response = await ChannelApi.connectFBChannel({
      body: { return_url: options?.returnURL ?? '' },
    })
    return response.data
  },
)

export const getListSuspectedFbPolicyOffenders = createAsyncAction(
  'application/getListSuspectedFbPolicyOffenders',
  async () => {
    const response = await AccountHealthApi.listSuspectedFbPolicyOffenders()
    return response.data
  },
)

export const updateSkipPreviewModal = (status: boolean) => ({
  type: UPDATE_SKIP_PREVIEW_ONBOARDING_MODAL,
  previewOnboardingModalStatus: status,
})

export const setAccountDisplayFlag = (
  display_flag: AccountDisplayFlag,
  value: boolean,
): IAsyncThunkAction => {
  return async (dispatch) => {
    dispatch({
      type: atypes.ACCOUNT_SET_DISPLAY_FLAG,
    })
    try {
      await AccountApi.setDisplayFlag({
        body: {
          display_flag,
          value,
        },
      })

      return dispatch({
        type: atypes.ACCOUNT_SET_DISPLAY_FLAG_RESPONSE,
        display_flag,
        value,
      })
    } catch (error) {
      handleError(error)
      return dispatch({
        type: atypes.ACCOUNT_SET_DISPLAY_FLAG_ERROR,
      })
    }
  }
}

export const updateAsyncBillingActionStatus =
  (data: AsyncBillingPaymentSocket): IThunkAction =>
  (dispatch, getState) => {
    const { user_id: userId, status, invoice_confirmation_url } = data.model

    const state = getState()
    const currentUserId = getUserId(state)

    if (currentUserId !== userId) return

    dispatch({
      type: atypes.CURRENT_ACCOUNT_UPDATE_ASYNC_BILLING_ACTIONS,
      status,
      url: invoice_confirmation_url,
    })
  }

anotherTabNotificationsListener.on('account_updated', (data: AccountUpdatedData, dispatch) => {
  if (data.model?.threads_statuses) {
    dispatch(updateThreadsStats(data.model.threads_statuses))
  }

  if (data.model.connected_integrations) {
    const { connected_integrations } = data.model
    dispatch(updateConnectedIntegrations({ connected_integrations }))
  }
})

anotherTabNotificationsListener.on(
  'account_pro_status_blocked',
  (data: AccountProStatusBlockedData, dispatch) => {
    dispatch(setProStatusBlocked(data))
  },
)

anotherTabNotificationsListener.on('account_pro_status_unblocked', ({ pro_status }, dispatch) => {
  if (pro_status && pro_status !== 'null') dispatch(setProStatusUnblocked(pro_status))
})

anotherTabNotificationsListener.on(
  'account_fb_policy_enforcement_notification_new',
  (data: AccountFbPolicyNotificationNewData, dispatch) => {
    dispatch(handleNewPolicyEnforcementNotifications(data))
  },
)

anotherTabNotificationsListener.on(
  'account_fb_policy_enforcement_notification_read',
  (data: StandardWebSocketData, dispatch) => {
    dispatch(handleReadPolicyEnforcementNotifications(data))
  },
)
anotherTabNotificationsListener.on(
  'highlight_logs_on_fb_policy_enforcement',
  (data: StandardWebSocketData, dispatch) => {
    dispatch(handleHighlightLogsOnFbPolicyEnforcementNotification())
  },
)

anotherTabNotificationsListener.on('rn_flow_created', (data, dispatch) => {
  dispatch(setRnCreatedFlag(data.model.channel))
})

anotherTabNotificationsListener.on(WebSocketEvents.FACEBOOK_MAIN_MENU_UNINSTALLATION_FAILED, () => {
  alert(
    l.translate("Your page connection is lost, and changes to Main Menu can't be saved"),
    'warning',
    { isTemporary: false },
  )
})

accountNotificationsListener.on(WebSocketEvents.INVOICE_NEXT_ACTION_REQUIRED, (data, dispatch) => {
  dispatch(updateAsyncBillingActionStatus(data))
})

accountNotificationsListener.on(
  WebSocketEvents.WA_CHANNEL_DISCONNECT_COMPLETED,
  async (_, dispatch) => {
    const { payload: channelData } = await dispatch(getChannelData())
    dispatch({ type: atypes.CURRENT_ACCOUNT_UPDATE_WHATSAPP_CHANNEL, data: channelData })
  },
)
